import React, { useMemo, useEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Html, Line } from "@react-three/drei";
import SatGizmo from "./SatGizmoAxes";
import * as THREE from "three";
import FloorDotGrid from "../../../../../../../simulate/my-scenario/components/scenario-timeline/thermal-heatmap/components/visualization/FloorDotGrid";
import { useThermalDesign } from "../../../../ThermalDesignContext";
import { THERMAL_FACE_BODY_VEC, THERMAL_FACE_ORDER } from "../../../../ThermalDesignContext";
import type { ThermalFaceId, CoatingCell } from "../../../../ThermalDesignTypes";
import { TopCylinder, BottomCylinder, Telescope } from "./SubsystemRenderer";
import SubsystemRenderer from "./SubsystemRenderer";
import {
  SUBSYSTEM_COLORS,
  SUBSYSTEM_OPACITY,
  STRUCTURAL_COLORS,
  STRUCTURAL_OPACITY,
} from "../../types/subsystems";

// ---- Coating canvas texture helpers ----

const CELL_PX = 64; // canvas pixels per cell
const CANVAS_PX = CELL_PX * 3; // 192×192 canvas

function drawDiagonalHatch(ctx: CanvasRenderingContext2D, x: number, y: number, color: string) {
    const spacing = 8;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, CELL_PX, CELL_PX);
    ctx.clip();
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.globalAlpha = 0.88;
    for (let d = -CELL_PX; d < CELL_PX * 2; d += spacing) {
        // Diagonal hatch (NE direction only)
        ctx.beginPath();
        ctx.moveTo(x + d, y);
        ctx.lineTo(x + d + CELL_PX, y + CELL_PX);
        ctx.stroke();
    }
    ctx.restore();
}

function createCoatingTexture(cells: CoatingCell[]): THREE.CanvasTexture {
    const canvas = document.createElement("canvas");
    canvas.width = CANVAS_PX;
    canvas.height = CANVAS_PX;
    const ctx = canvas.getContext("2d")!;

    cells.forEach((cell, idx) => {
        const col = idx % 3;
        // cells[0] is bottom-left in 3D; with THREE flipY=true, canvas row 0 = top of face
        // so bottom-of-face cells (gridRow 0) go to canvas row 2
        const gridRow = Math.floor(idx / 3);
        const canvasRow = 2 - gridRow;
        const cx = col * CELL_PX;
        const cy = canvasRow * CELL_PX;

        if (cell === "none") {
            // Transparent - no fill applied
        } else if (cell === "mli") {
            drawDiagonalHatch(ctx, cx, cy, "#F2A93B");
        } else if (cell === "osr") {
            drawDiagonalHatch(ctx, cx, cy, "#83CF5E");
        }
    });

    const tex = new THREE.CanvasTexture(canvas);
    return tex;
}

// Dot positions in face-local 2D space for 1-node and 9-node distributions
const DOT_OFFSETS_1: [number, number][] = [[0, 0]];
const GRID_D = 5 / 3; // cell centre offset for 3×3 grid on a 5-unit face
const DOT_OFFSETS_9: [number, number][] = [
    [-GRID_D, -GRID_D], [0, -GRID_D], [GRID_D, -GRID_D],
    [-GRID_D,  0     ], [0,  0     ], [GRID_D,  0     ],
    [-GRID_D,  GRID_D], [0,  GRID_D], [GRID_D,  GRID_D],
];

// Two internal horizontal decks at ±5/6 (~±0.833), spanning full XZ extent
const DECK_Y = 5 / 6;

// 3×3 grid geometry: 2 internal vertical + 2 internal horizontal lines on a 5×5 face
const GRID_3X3 = (() => {
    const e = 5 / 6; // cell boundary offset: divides 5-unit face into 3 equal columns/rows
    const h = 2.5;   // half-face extent
    const pts = [
        -e, -h, 0,  -e,  h, 0,  // left vertical divider
         e, -h, 0,   e,  h, 0,  // right vertical divider
        -h, -e, 0,   h, -e, 0,  // bottom horizontal divider
        -h,  e, 0,   h,  e, 0,  // top horizontal divider
    ];
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    return g;
})();

const InternalDecks: React.FC = () => {
    const { nodeSpecsOptions, showNodes, showSubsystemView } = useThermalDesign();
    const nodesPerFace = (nodeSpecsOptions[0]?.nodes_per_face ?? 1) as 1 | 9;
    const selectedNode = nodeSpecsOptions.find((n) => n.isSelected);
    // INT1 = lower deck (-DECK_Y), INT2 = upper deck (+DECK_Y)
    const selectedDeckId = selectedNode?.values?.node_isInternal ? selectedNode.id : null;

    const deckEdges = useMemo(() => new THREE.EdgesGeometry(new THREE.PlaneGeometry(5, 5)), []);
    const dotRadius = nodesPerFace === 9 ? 0.12 : 0.18;
    const dotGeo = useMemo(() => new THREE.CircleGeometry(dotRadius, 16), [dotRadius]);
    const dotOffsets = nodesPerFace === 9 ? DOT_OFFSETS_9 : DOT_OFFSETS_1;

    return (
        <>
            {([-DECK_Y, DECK_Y] as const).map((y, idx) => {
                const deckId = idx === 0 ? "INT1" : "INT2";
                const isSelected = selectedDeckId === deckId;

                const fillColor = isSelected ? STRUCTURAL_COLORS.ACTIVE : STRUCTURAL_COLORS.INACTIVE;
                const edgeColor = isSelected ? STRUCTURAL_COLORS.ACTIVE_EDGE : STRUCTURAL_COLORS.INACTIVE_EDGE;
                const dotColor = isSelected ? STRUCTURAL_COLORS.ACTIVE_EDGE : STRUCTURAL_COLORS.INACTIVE_EDGE;
                
                let fillOpacity: number = isSelected ? STRUCTURAL_OPACITY.ACTIVE_FILL : STRUCTURAL_OPACITY.INACTIVE_FILL;
                let edgeOpacity: number = isSelected ? STRUCTURAL_OPACITY.ACTIVE_EDGE : STRUCTURAL_OPACITY.INACTIVE_EDGE;
                let dotOpacity: number = isSelected ? STRUCTURAL_OPACITY.ACTIVE_DOT : STRUCTURAL_OPACITY.INACTIVE_DOT;
                
                if (showSubsystemView) {
                  fillOpacity = isSelected ? STRUCTURAL_OPACITY.ACTIVE_FILL : SUBSYSTEM_OPACITY.UNSELECTED_FACE;
                  edgeOpacity = isSelected ? STRUCTURAL_OPACITY.ACTIVE_EDGE : SUBSYSTEM_OPACITY.UNSELECTED_FACE;
                  dotOpacity = isSelected ? STRUCTURAL_OPACITY.ACTIVE_DOT : SUBSYSTEM_OPACITY.UNSELECTED_FACE;
                }

                return (
                    <group key={y} position={[0, y, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                        <mesh>
                            <planeGeometry args={[5, 5]} />
                            <meshBasicMaterial color={fillColor} transparent opacity={fillOpacity} side={THREE.DoubleSide} depthWrite={false} />
                        </mesh>
                        <lineSegments geometry={deckEdges}>
                            <lineBasicMaterial color={edgeColor} transparent opacity={edgeOpacity} />
                        </lineSegments>
                        {showNodes && dotOffsets.map(([dx, dy], i) => (
                            <mesh key={i} geometry={dotGeo} position={[dx, dy, 0.004]}>
                                <meshBasicMaterial color={dotColor} transparent opacity={dotOpacity} />
                            </mesh>
                        ))}
                        {showNodes && nodesPerFace === 9 && isSelected && (
                            <lineSegments geometry={GRID_3X3}>
                                <lineBasicMaterial color={edgeColor} transparent opacity={edgeOpacity * 0.75} />
                            </lineSegments>
                        )}
                    </group>
                );
            })}
        </>
    );
};

// Axes scaled for 5×5×5 cube
const SAT_AXES = [
    { dir: [0, -1, 0] as [number, number, number], color: "#FF3333", label: "X", annotation: "Yaw" },
    { dir: [1,  0, 0] as [number, number, number], color: "#CCF54E", label: "Y", annotation: "Roll" },
    { dir: [0,  0, 1] as [number, number, number], color: "#47B7FF", label: "Z", annotation: "Pitch" },
];
const S_SHAFT = 6.0;
const S_HEAD  = 0.45;
const S_HRAD  = 0.08;
const S_SRAD  = 0.025;

const SatAxes: React.FC = () => {
    const { showAxisLabels, showNadirLabel } = useThermalDesign();
    return (
    <group>
        {SAT_AXES.map(({ dir, color, label, annotation }) => {
            const isNadirAxis = label === "X";
            const displayLabel = isNadirAxis && showNadirLabel ? "Nadir" : `+${label}`;
            const showAnnotation = showAxisLabels && !(isNadirAxis && showNadirLabel);
            const [dx, dy, dz] = dir;
            const quatP = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(dx, dy, dz));
            const rotP  = new THREE.Euler().setFromQuaternion(quatP);
            const rotationP: [number, number, number] = [rotP.x, rotP.y, rotP.z];
            const quatN = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(-dx, -dy, -dz));
            const rotN  = new THREE.Euler().setFromQuaternion(quatN);
            const rotationN: [number, number, number] = [rotN.x, rotN.y, rotN.z];
            const conePosP:  [number, number, number] = [dx * (S_SHAFT / 2 + S_HEAD / 2),  dy * (S_SHAFT / 2 + S_HEAD / 2),  dz * (S_SHAFT / 2 + S_HEAD / 2)];
            const conePosN:  [number, number, number] = [-dx * (S_SHAFT / 2 + S_HEAD / 2), -dy * (S_SHAFT / 2 + S_HEAD / 2), -dz * (S_SHAFT / 2 + S_HEAD / 2)];
            const labelPosP: [number, number, number] = [dx * (S_SHAFT / 2 + S_HEAD + 0.35), dy * (S_SHAFT / 2 + S_HEAD + 0.35), dz * (S_SHAFT / 2 + S_HEAD + 0.35)];
            const labelPosN: [number, number, number] = [-dx * (S_SHAFT / 2 + S_HEAD + 0.35), -dy * (S_SHAFT / 2 + S_HEAD + 0.35), -dz * (S_SHAFT / 2 + S_HEAD + 0.35)];
            const labelStyle: React.CSSProperties = { color, fontSize: 12, fontWeight: 700, fontFamily: "Montserrat, sans-serif", userSelect: "none", pointerEvents: "none" };
            return (
                <group key={label}>
                    <mesh rotation={rotationP}>
                        <cylinderGeometry args={[S_SRAD, S_SRAD, S_SHAFT, 8]} />
                        <meshBasicMaterial color={color} />
                    </mesh>
                    <mesh position={conePosP} rotation={rotationP}>
                        <coneGeometry args={[S_HRAD, S_HEAD, 8]} />
                        <meshBasicMaterial color={color} />
                    </mesh>
                    <Html center position={labelPosP} zIndexRange={[0, 0]}>
                        <span style={labelStyle}>{displayLabel}{showAnnotation && <span style={{ fontSize: 8, fontWeight: 400, marginLeft: 2 }}>({annotation})</span>}</span>
                    </Html>
                    <mesh position={conePosN} rotation={rotationN}>
                        <coneGeometry args={[S_HRAD, S_HEAD, 8]} />
                        <meshBasicMaterial color={color} />
                    </mesh>
                    <Html center position={labelPosN} zIndexRange={[0, 0]}><span style={labelStyle}>−{label}</span></Html>
                </group>
            );
        })}
    </group>
    );
};

// Individual face planes — selected face is orange, unselected is dark blue
const FACE_PLANE_CONFIG: Record<ThermalFaceId, { pos: [number, number, number]; rot: [number, number, number] }> = {
    "+X": { pos: [0, -2.501, 0],  rot: [ Math.PI / 2, 0, 0] },  // Bottom
    "-X": { pos: [0,  2.501, 0],  rot: [-Math.PI / 2, 0, 0] },  // Top
    "+Y": { pos: [ 2.501, 0, 0],  rot: [0,  Math.PI / 2, 0] },  // Right
    "-Y": { pos: [-2.501, 0, 0],  rot: [0, -Math.PI / 2, 0] },  // Left
    "+Z": { pos: [0, 0,  2.501],  rot: [0, 0, 0] },              // Front
    "-Z": { pos: [0, 0, -2.501],  rot: [0, Math.PI, 0] },        // Back
};

const FacePlanes: React.FC = () => {
    const { nodeSpecsOptions, showNodes, showCoatingView, showSubsystemView, openModal } = useThermalDesign();
    const selectedNode = nodeSpecsOptions.find((n) => n.isSelected);
    const nodesPerFace = (nodeSpecsOptions[0]?.nodes_per_face ?? 1) as 1 | 9;

    const selectedFace: ThermalFaceId | null = useMemo(() => {
        if (!selectedNode || selectedNode.values?.node_isInternal) return null;
        const vecKey = (selectedNode.values?.node_body_vec ?? []).join(",");
        return THERMAL_FACE_ORDER.find((id) => THERMAL_FACE_BODY_VEC[id].join(",") === vecKey) ?? null;
    }, [selectedNode]);

    const planeEdges = useMemo(() => new THREE.EdgesGeometry(new THREE.PlaneGeometry(5, 5)), []);
    const dotRadius = nodesPerFace === 9 ? 0.12 : 0.18;
    const dotGeo = useMemo(() => new THREE.CircleGeometry(dotRadius, 16), [dotRadius]);
    const dotOffsets = nodesPerFace === 9 ? DOT_OFFSETS_9 : DOT_OFFSETS_1;

    // Build per-face cells array from context (default all "none" if coating not configured)
    const faceCoatingCells = useMemo<Map<ThermalFaceId, CoatingCell[]>>(() => {
        const map = new Map<ThermalFaceId, CoatingCell[]>();
        THERMAL_FACE_ORDER.forEach((faceId) => {
            const faceNode = nodeSpecsOptions.find((n) => {
                const vecKey = (n.values?.node_body_vec ?? []).join(",");
                return THERMAL_FACE_BODY_VEC[faceId].join(",") === vecKey;
            });
            const cells = faceNode?.values?.coating?.cells;
            map.set(
                faceId,
                Array.isArray(cells) && cells.length === 9
                    ? (cells as CoatingCell[])
                    : (Array(9).fill("none") as CoatingCell[])
            );
        });
        return map;
    }, [nodeSpecsOptions]);

    // Create canvas textures per face when coating view is active
    const coatingTextures = useMemo<Map<ThermalFaceId, THREE.CanvasTexture>>(() => {
        if (!showCoatingView) return new Map();
        const texMap = new Map<ThermalFaceId, THREE.CanvasTexture>();
        THERMAL_FACE_ORDER.forEach((faceId) => {
            const cells = faceCoatingCells.get(faceId) ?? (Array(9).fill("none") as CoatingCell[]);
            texMap.set(faceId, createCoatingTexture(cells));
        });
        return texMap;
    }, [showCoatingView, faceCoatingCells]);

    // Dispose old textures on unmount or when they change
    useEffect(() => {
        return () => {
            coatingTextures.forEach((tex) => tex.dispose());
        };
    }, [coatingTextures]);

    return (
        <>
            {THERMAL_FACE_ORDER.map((faceId) => {
                const { pos, rot } = FACE_PLANE_CONFIG[faceId];
                const isSelected  = selectedFace === faceId;
                
                // In subsystem view: selected keeps original opacity, unselected become very transparent
                let fillOpacity: number;
                let edgeOpacity: number;
                let dotOpacity: number;
                let fillColor: string;
                let edgeColor: string;
                let dotColor: string;
                
                if (showSubsystemView) {
                  fillOpacity = isSelected ? STRUCTURAL_OPACITY.ACTIVE_FILL : SUBSYSTEM_OPACITY.UNSELECTED_FACE;
                  edgeOpacity = isSelected ? STRUCTURAL_OPACITY.ACTIVE_EDGE : SUBSYSTEM_OPACITY.UNSELECTED_FACE;
                  dotOpacity = isSelected ? STRUCTURAL_OPACITY.ACTIVE_DOT : SUBSYSTEM_OPACITY.UNSELECTED_FACE;
                  fillColor = isSelected ? STRUCTURAL_COLORS.ACTIVE : STRUCTURAL_COLORS.INACTIVE;
                  edgeColor = isSelected ? STRUCTURAL_COLORS.ACTIVE_EDGE : STRUCTURAL_COLORS.INACTIVE_EDGE;
                  dotColor = isSelected ? STRUCTURAL_COLORS.ACTIVE_EDGE : STRUCTURAL_COLORS.INACTIVE_EDGE;
                } else {
                  fillColor = isSelected ? STRUCTURAL_COLORS.ACTIVE : STRUCTURAL_COLORS.INACTIVE;
                  fillOpacity = isSelected ? STRUCTURAL_OPACITY.ACTIVE_FILL : STRUCTURAL_OPACITY.INACTIVE_FILL;
                  edgeColor = isSelected ? STRUCTURAL_COLORS.ACTIVE_EDGE : STRUCTURAL_COLORS.INACTIVE_EDGE;
                  edgeOpacity = isSelected ? STRUCTURAL_OPACITY.ACTIVE_EDGE : STRUCTURAL_OPACITY.INACTIVE_EDGE;
                  dotColor = isSelected ? STRUCTURAL_COLORS.ACTIVE_EDGE : STRUCTURAL_COLORS.INACTIVE_EDGE;
                  dotOpacity = isSelected ? STRUCTURAL_OPACITY.ACTIVE_DOT : STRUCTURAL_OPACITY.INACTIVE_DOT;
                }
                
                const coatingTex  = coatingTextures.get(faceId);
                const showCoatingOnFace = showCoatingView && isSelected && !!coatingTex;
                return (
                    <group key={faceId} position={pos} rotation={rot}>
                        {showCoatingOnFace ? (
                            <mesh>
                                <planeGeometry args={[5, 5]} />
                                <meshBasicMaterial map={coatingTex} transparent side={THREE.DoubleSide} depthWrite={false} />
                            </mesh>
                        ) : (
                            <mesh>
                                <planeGeometry args={[5, 5]} />
                                <meshBasicMaterial color={fillColor} transparent opacity={fillOpacity} side={THREE.DoubleSide} depthWrite={false} />
                            </mesh>
                        )}
                        <lineSegments geometry={planeEdges}>
                            <lineBasicMaterial color={edgeColor} transparent opacity={edgeOpacity} />
                        </lineSegments>
                        {!showSubsystemView && (
                          <>
                            {!showCoatingView && showNodes && dotOffsets.map(([dx, dy], i) => (
                                <mesh key={i} geometry={dotGeo} position={[dx, dy, 0.004]}>
                                    <meshBasicMaterial color={dotColor} transparent opacity={dotOpacity} />
                                </mesh>
                            ))}
                            {(showCoatingOnFace || (showNodes && nodesPerFace === 9 && isSelected) || (openModal.isConfigureCoatingModalOpen && isSelected && nodesPerFace === 9)) && (
                                <lineSegments geometry={GRID_3X3}>
                                    <lineBasicMaterial
                                        color={STRUCTURAL_COLORS.ACTIVE}
                                        transparent
                                        opacity={edgeOpacity}
                                        linewidth={3}
                                    />
                                </lineSegments>
                            )}
                            {openModal.isConfigureCoatingModalOpen && isSelected && nodesPerFace === 9 && DOT_OFFSETS_9.map(([dx, dy], i) => (
                                <Html key={`cell-${i}`} center position={[dx, dy, 0.05]} zIndexRange={[0, 0]}>
                                    <span style={{ fontSize: 10, fontWeight: 700, color: STRUCTURAL_COLORS.ACTIVE, fontFamily: "Montserrat, sans-serif", userSelect: "none", pointerEvents: "none" }}>{i + 1}</span>
                                </Html>
                            ))}
                          </>
                        )}
                    </group>
                );
            })}
        </>
    );
};

const SolarPanels: React.FC = () => {
    const panelEdges = useMemo(() => new THREE.EdgesGeometry(new THREE.PlaneGeometry(5, 5)), []);

    const cellGrid = useMemo(() => {
        const pts: number[] = [];
        // 5×5 grid of small squares, 0.5×0.5 each, 0.25 gap, 0.75 padding from edges
        // Drawable area: -1.75 to +1.75 (3.5 wide), step = 0.75
        const size = 0.5;
        const step = 0.75; // square size + gap
        const start = -1.75;
        const count = 5;
        for (let row = 0; row < count; row++) {
            for (let col = 0; col < count; col++) {
                const x0 = start + col * step;
                const y0 = start + row * step;
                const x1 = x0 + size;
                const y1 = y0 + size;
                // 4 edges of the square as line segment pairs
                pts.push(x0, y0, 0,  x1, y0, 0);
                pts.push(x1, y0, 0,  x1, y1, 0);
                pts.push(x1, y1, 0,  x0, y1, 0);
                pts.push(x0, y1, 0,  x0, y0, 0);
            }
        }
        const geo = new THREE.BufferGeometry();
        geo.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
        return geo;
    }, []);

    return (
        <>
            {([-5, 5] as const).map((x) => (
                <group key={x} position={[x, 2.501, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                    <mesh>
                        <planeGeometry args={[5, 5]} />
                        <meshBasicMaterial color="#0d1f2d" transparent opacity={0.9} side={THREE.DoubleSide} />
                    </mesh>
                    <lineSegments geometry={panelEdges}>
                        <lineBasicMaterial color="#5aabcc" transparent opacity={0.85} />
                    </lineSegments>
                    <lineSegments geometry={cellGrid}>
                        <lineBasicMaterial color="#2a6a8a" transparent opacity={0.6} />
                    </lineSegments>
                </group>
            ))}
        </>
    );
};

const SceneContent: React.FC = () => {
    const { showOrientationGizmo, showSubsystemView } = useThermalDesign();
    return (
        <>
            <ambientLight intensity={1} />
            <FloorDotGrid />
            <FacePlanes />
            {!showSubsystemView && <SolarPanels />}
            <InternalDecks />
            {!showSubsystemView && <TopCylinder />}
            {!showSubsystemView && <BottomCylinder />}
            {!showSubsystemView && <Telescope />}
            <SubsystemRenderer />
            {!showOrientationGizmo && <SatAxes />}
            <OrbitControls makeDefault minDistance={3} maxDistance={30} target={[0, 0, 0]} />
            {showOrientationGizmo && <SatGizmo />}
        </>
    );
};

const SatCubePreview: React.FC = () => (
    <div style={{ position: "absolute", inset: 0, background: "#000" }}>
        <Canvas
            camera={{ position: [-4, 4.5, 12], fov: 105 }}
            gl={{ antialias: true, alpha: true }}
            style={{ background: "transparent", position: "absolute", inset: 0 }}
        >
            <SceneContent />
        </Canvas>
    </div>
);

export default SatCubePreview;
